import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Box, 
         Button, 
         FormControl, 
         FormErrorMessage, 
         FormLabel, 
         FormHelperText, 
         Input, 
         Textarea, 
         VStack, 
         HStack, 
         Text, 
         Grid, 
         Spinner,
         Checkbox } from '@chakra-ui/react'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'

interface HolidayFormProps {
  holidayId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

type HolidayFormValues = {
  name: string
  holiday_date: string
  is_recurring: boolean
}

export function HolidayForm({ holidayId, onSuccess, onCancel }: HolidayFormProps) {
  const navigate = useNavigate()
  const { showError, showSuccess } = useToastNotification()

  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isEditMode = !!holidayId

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<HolidayFormValues>({
  })

  useEffect(() => {
    if (isEditMode && holidayId) {
      setIsLoading(true)
      apiClient
        .get(`/service-catalog/holidays/${holidayId}`)
        .then(({ data }) => {
          const holidayData = data?.data || data || {}

          let parentId: number | null = null
          if (holidayData.parent_id) {
            parentId = holidayData.parent_id
          } else if (holidayData.parent_holiday?.id) {
            parentId = holidayData.parent_holiday.id
          }

          reset({
            name: holidayData.name || '',
            holiday_date: holidayData.holiday_date
                ? holidayData.holiday_date.split('T')[0]
                : '',
            is_recurring: holidayData.is_recurring || false,
          })
        })
        .catch((error: any) => {
          showError(error)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [holidayId, isEditMode, reset, showError])

  const onSubmit = async (data: HolidayFormValues) => {
    setIsSubmitting(true)

    try {
      const payload = {
        name: data.name,
        holiday_date: data.holiday_date,
        is_recurring: data.is_recurring,
      }

      if (isEditMode) {
        await apiClient.put(`/service-catalog/holidays/${holidayId}`, payload)
        showSuccess('Success', 'Holiday updated successfully')
      } else {
        await apiClient.post('/service-catalog/holidays', payload)
        showSuccess('Success', 'Holiday created successfully')
      }

      if (onSuccess) {
        onSuccess()
      } else {
        navigate('/service-catalog/holidays')
      }
    } catch (error: any) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      navigate('/service-catalog/holidays')
    }
  }

  if (isLoading) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="xl" color="blue.500" />
        <Text>Loading holiday...</Text>
      </VStack>
    )
  }

  return (
      <Box as="form" onSubmit={handleSubmit(onSubmit)}>
        <VStack spacing={4} align="stretch">
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
              Holiday Name <Text as="span" color="red.500">*</Text>
            </FormLabel>
            <FormControl isInvalid={!!errors.name}>
              <Input {...register('name')} placeholder="e.g. Technology, Business, Health" />
              <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
              <FormHelperText>The name is how it appears on your site.</FormHelperText>
            </FormControl>
          </Grid>
  
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
                Holiday Date <Text as="span" color="red.500">*</Text>
            </FormLabel>

            <FormControl isInvalid={!!errors.holiday_date}>
                <Input
                type="date"
                {...register('holiday_date')}
                />

                <FormErrorMessage>
                {errors.holiday_date?.message}
                </FormErrorMessage>

                <FormHelperText>
                The date when the holiday occurs.
                </FormHelperText>
            </FormControl>
          </Grid>
  
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
              Is it Recurring?
            </FormLabel>
            <FormControl isInvalid={!!errors.is_recurring}>
              <Checkbox {...register('is_recurring')} />
              <FormErrorMessage>{errors.is_recurring?.message}</FormErrorMessage>
              <FormHelperText>Check if the holiday is recurring.</FormHelperText>
            </FormControl>
          </Grid>
  
          <HStack spacing={4} justify="flex-end" pt={4}>
            <Button variant="outline" onClick={handleCancel} isDisabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              colorScheme="blue"
              isLoading={isSubmitting}
              loadingText={isEditMode ? 'Saving...' : 'Creating...'}
            >
              {isEditMode ? 'Save' : 'Create Holiday'}
              {isSubmitting && (
                <Text as="span" ml={2} fontSize="sm">
                  ...
                </Text>
              )}
            </Button>
          </HStack>
        </VStack>
      </Box>
    )
}